import { useEffect, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router';
import { AuthSessionError } from './auth/AuthSessionError';
import { hasTokenRequestFailed, initKeycloak, keycloak } from './auth/keycloak';
import { useIdentityVerification } from './features/identityVerification/hooks/useIdentityVerification';
import IdentityVerificationGate from './features/identityVerification/components/IdentityVerificationModal';
import { useTermsAndConditions } from './features/termsAndConditions/hooks/useTermsAndConditions';
import TermsAndConditionsGate from './features/termsAndConditions/components/TermsAndConditionsModal';
import AppRoutes from './routes/AppRoutes';
import CommissionUpdateBannerModal from './shared/components/CommissionUpdateBannerModal';
import { useCommissionUpdateBannerModal } from './shared/hooks/useCommissionUpdateBannerModal';
import { useUserInfo } from './shared/hooks/user/useUserInfo';
import { useSyncOnboardingWithMedia } from './store/onboardingStore';

function AppShell() {
  const { pathname } = useLocation();
  const { fetchUserInfo } = useUserInfo();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [authFailed, setAuthFailed] = useState(() => hasTokenRequestFailed());
  const { isBlocking: isTermsBlocking, isLoading: isTermsLoading } = useTermsAndConditions();
  const { isBlocking: isIdentityBlocking } = useIdentityVerification();
  const isBlockingGateActive = isTermsLoading || isTermsBlocking || isIdentityBlocking;
  const { isOpen: isCommissionBannerOpen, dismiss: dismissCommissionBanner } =
    useCommissionUpdateBannerModal(
      authCheckComplete && isAuthenticated && !isBlockingGateActive
    );

  useSyncOnboardingWithMedia(true);

  useEffect(() => {
    let cancelled = false;

    const runAuthCheck = async () => {
      try {
        const authenticated = await initKeycloak();
        if (cancelled) return;

        setIsAuthenticated(Boolean(authenticated));
        if (authenticated) {
          setAuthFailed(false);
          fetchUserInfo();
        } else if (hasTokenRequestFailed()) {
          setAuthFailed(true);
        }
      } catch {
        if (cancelled) return;
        setIsAuthenticated(false);
        setAuthFailed(true);
      } finally {
        if (!cancelled) {
          setAuthCheckComplete(true);
        }
      }
    };

    runAuthCheck();

    return () => {
      cancelled = true;
    };
  }, [fetchUserInfo]);

  useEffect(() => {
    if (!authCheckComplete || authFailed || hasTokenRequestFailed()) return;
    if (!keycloak.authenticated) {
      keycloak.login();
    }
  }, [authCheckComplete, authFailed, pathname]);

  if (authCheckComplete && authFailed) {
    return <AuthSessionError />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <TermsAndConditionsGate>
      <IdentityVerificationGate>
        <>
          <CommissionUpdateBannerModal
            isOpen={isCommissionBannerOpen}
            onClose={dismissCommissionBanner}
          />
          <AppRoutes />
        </>
      </IdentityVerificationGate>
    </TermsAndConditionsGate>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
